import { useState } from "react";
import { Check, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setPaymentPart, sendInvoice } from "../lib/api";
import { partState, money, fmtDay } from "../lib/payments";
import { FeeAdjust } from "./FeeAdjust";

const TAG = {
  paid: "bg-emerald-950/60 text-emerald-300 border-emerald-600/60",
  overdue: "bg-red-950/60 text-red-300 border-red-600/60",
  today: "bg-amber-950/60 text-amber-300 border-amber-500/60",
  upcoming: "bg-slate-800 text-slate-300 border-slate-600",
  unset: "bg-transparent text-slate-500 border-[#262C38]",
};
const TAG_LABEL = { paid: "Paid", overdue: "Overdue", today: "Due today", upcoming: "Upcoming", unset: "Set start date" };

export const PaymentPart = ({ payment, part, label, onChange, dirty = false }) => {
  const info = payment.parts[part];
  const [busy, setBusy] = useState("");
  const state = partState(info);
  const isCarrier = part.startsWith("carrier");

  const run = async (kind, fn, okMsg) => {
    setBusy(kind);
    try {
      const { data } = await fn();
      onChange(data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Something went wrong");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid={`payment-part-${part}-${payment.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2 border-t border-[#262C38] first:border-t-0">
      <button
        data-testid={`payment-part-paid-toggle-${part}-${payment.id}`}
        aria-pressed={!!info.paid_at}
        disabled={!!busy}
        onClick={() => run("paid", async () => {
          const res = await setPaymentPart(payment.id, part, !info.paid_at);
          if (res.data.receipt === "sent") toast.success(`Thank-you receipt sent to ${payment.carrier_email}`);
          else if (res.data.receipt) toast.error(`Receipt not sent: ${res.data.receipt.replace("failed: ", "")}`);
          return res;
        })}
        title={info.paid_at ? "Mark as not paid" : isCarrier ? "Mark received" : "Mark sent"}
        className={`h-5 w-5 shrink-0 rounded-sm border flex items-center justify-center transition-colors disabled:opacity-50 ${info.paid_at ? "bg-emerald-600 border-emerald-500" : "border-slate-500 hover:border-white"}`}
      >
        {busy === "paid" ? <Loader2 className="h-3 w-3 animate-spin" /> : info.paid_at && <Check className="h-3.5 w-3.5 text-white" />}
      </button>
      <div className="min-w-[120px] flex-1">
        <p className="text-xs text-white">{label}</p>
        <p className="font-mono text-[10px] text-slate-500">
          Due {fmtDay(info.due_date)}{info.paid_at ? ` · ${isCarrier ? "received" : "sent"} ${fmtDay(info.paid_at)}` : ""}
        </p>
        {isCarrier && info.working_days_late > 0 && (info.late_fee > 0 || !info.paid_at) && (
          <p data-testid={`payment-late-fee-${part}-${payment.id}`} className={`font-mono text-[10px] ${info.late_fee ? "text-red-300" : "text-amber-300"}`}>
            {info.late_fee
              ? `Late fee ${money(info.late_fee)} (${info.fee_override ? "agreed deal + 3%/day after" : `${info.penalty_days} day${info.penalty_days === 1 ? "" : "s"} × 3%`}) · total ${money(info.total_due)}`
              : info.fee_override ? "Late fee waived" : "Reminder day — no fee yet"}
          </p>
        )}
      </div>
      <span data-testid={`payment-part-amount-${part}-${payment.id}`} className="font-mono text-sm text-white">{money(info.amount)}</span>
      <span data-testid={`payment-part-tag-${part}-${payment.id}`} className={`text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${TAG[state]}`}>{TAG_LABEL[state]}</span>
      {isCarrier && !info.paid_at && (
        <button
          data-testid={`payment-send-invoice-btn-${part}-${payment.id}`}
          disabled={!!busy || dirty}
          title={dirty ? "Save details first" : "Email this invoice to the carrier now"}
          onClick={() => run("inv", () => sendInvoice(payment.id, part), `Invoice sent to ${payment.carrier_email}`)}
          className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded border border-[#384152] text-slate-200 hover:border-[#F95722] disabled:opacity-50"
        >
          {busy === "inv" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          {info.invoice_sent_at ? "Resend" : "Send invoice"}
        </button>
      )}
      {isCarrier && (info.penalty_days > 0 || info.fee_override) && (
        <FeeAdjust payment={payment} part={part} info={info} onChange={onChange} />
      )}
      {isCarrier && info.receipt_sent_at && (
        <span data-testid={`payment-receipt-sent-${part}-${payment.id}`} className="w-full pl-8 font-mono text-[10px] text-emerald-300">Receipt emailed {fmtDay(info.receipt_sent_at)}</span>
      )}
      {isCarrier && info.invoice_sent_at && (
        <span data-testid={`payment-invoice-sent-${part}-${payment.id}`} className="w-full pl-8 font-mono text-[10px] text-sky-300">Invoice emailed {fmtDay(info.invoice_sent_at)}</span>
      )}
    </div>
  );
};
